import {
  UserBlock,
  Text,
  Heading,
  Box,
  IconFa,
  ProgressBar,
} from "@cruk/cruk-react-components";

import { faClock, faCheckCircle } from "@fortawesome/free-regular-svg-icons";
import { faShoePrints, faLocationDot } from "@fortawesome/free-solid-svg-icons";

import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { useMounted } from "@fwa/src/hooks/useMounted";
import { useTimeAgo } from "@fwa/src/hooks/useTimeAgo";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/FeedItemFitbit/styles.module.css";

import { type FeedItemActivityFitbitType } from "@fwa/src/types";

const fitbitSVG = "/assets/images/logos/fitbit.svg";

export const FeedItemFitbit = ({
  feedItem,
  avatarUrl,
  displayName,
  fitbitDailyTargetSteps,
}: {
  feedItem: FeedItemActivityFitbitType;
  avatarUrl: string | undefined;
  displayName: string;
  fitbitDailyTargetSteps: number;
}) => {
  const mounted = useMounted();
  const timeString = useTimeAgo(feedItem.created);

  // Formatted after mount: server and browser timezones can differ.
  const feedItemDateString = mounted
    ? new Date(feedItem.created).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
  const fitbitPercentage = () =>
    !fitbitDailyTargetSteps
      ? 0
      : Math.floor((feedItem.steps / fitbitDailyTargetSteps) * 100);
  const [brandName] = useBrandContext();

  return (
    <div data-component="feed-item-fitbit">
      <Box marginBottom="xs">
        <UserBlock
          themeName={brandName}
          name={displayName}
          avatarUrl={avatarUrl}
          extra={
            <Text as="span" textColor="currentColor">
              <IconFa faIcon={faClock} /> {timeString}
            </Text>
          }
          size="m"
        />
      </Box>
      <Heading h3 marginTop="s" textSize="l" marginBottom="none">
        <span className={styles["logo-image-wrapper-span"]}>
          <img src={fitbitSVG} alt="" />
        </span>
        {` Fitbit activity - ${feedItemDateString}`}
      </Heading>
      <Box>
        <div className={sharedStyles["row-wrap-space-around"]}>
          <ProgressBar
            circleSize="128px"
            barColor={`var(--clr-cruk-cyan-700, #006da7)`}
            circleContents={
              <div className={sharedStyles["column"]}>
                <div className={styles["steps-wrapper"]}>
                  <IconFa faIcon={faShoePrints} color="sports-activity" />
                </div>
                <Text textAlign="center" marginBottom="none">
                  {feedItem.steps.toLocaleString()}
                </Text>
                <Text textAlign="center">steps</Text>
              </div>
            }
            percentage={fitbitPercentage()}
            isCircular
          />
          <ProgressBar
            circleSize="128px"
            barColor={`var(--clr-cruk-cyan-700, #006da7)`}
            circleContents={
              <div className={sharedStyles["column"]}>
                <div className={styles["marker-wrapper"]}>
                  <IconFa faIcon={faLocationDot} color="sports-activity" />
                </div>
                <Text textAlign="center" marginBottom="none">
                  {(feedItem.distance / 1000).toFixed(2)}
                </Text>
                <Text textAlign="center">km</Text>
              </div>
            }
            percentage={fitbitPercentage()}
            isCircular
          />
        </div>
        {!!fitbitDailyTargetSteps && (
          <Text
            marginTop="s"
            marginBottom="none"
            textColor={fitbitPercentage() < 100 ? "currentColor" : "success"}
          >
            {fitbitPercentage() >= 100 && (
              <>
                <IconFa faIcon={faCheckCircle} />{" "}
              </>
            )}
            {fitbitPercentage()}% of daily target achieved
          </Text>
        )}
      </Box>
    </div>
  );
};

export default FeedItemFitbit;
